import { useEffect, useState } from 'react';
import { RefreshCw, Mail, Calendar, Package, DollarSign, Zap, Clock, Eye } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import BoswellLogo from '@/components/common/BoswellLogo';
import EmailListOverlay from '@/components/common/EmailListOverlay';
import { useGenerateDigest, useLatestSecretaryReport, useMarkReportAsRead } from '@/hooks/useSecretary';
import ActionApprovalCard from '@/components/actions/ActionApprovalCard';
import EmailReferencesText from '@/components/common/EmailReferencesText';
import GenerateDigestDialog from '@/components/secretary/GenerateDigestDialog';

export default function SecretaryDigestWidget() {
  const [digest, setDigest] = useState(null);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showEmailOverlay, setShowEmailOverlay] = useState(false);
  const { data: latestReport, isLoading: latestLoading } = useLatestSecretaryReport();
  const generateDigestMutation = useGenerateDigest();
  const markAsRead = useMarkReportAsRead();

  // Load latest report on mount
  useEffect(() => {
    if (latestReport && !digest) {
      // Check if report is less than 22 hours old
      const reportAge = Date.now() - new Date(latestReport.generated_at).getTime();
      const twentyTwoHours = 22 * 60 * 60 * 1000;

      if (reportAge < twentyTwoHours) {
        setDigest(latestReport);
      }
    }
  }, [latestReport, digest]);

  // Mark report as read when displayed
  useEffect(() => {
    if (digest && !digest.read_at) {
      markAsRead.mutate(digest.id);
    }
  }, [digest?.id]);

  const handleGenerateDigest = async () => {
    // If no new emails and not confirmed, ask for confirmation
    if (!hasNewEmails && !confirmRefresh && digest) {
      setConfirmRefresh(true);
      setTimeout(() => setConfirmRefresh(false), 5000); // Reset after 5 seconds
      return;
    }

    setConfirmRefresh(false);
    // Open dialog instead of directly generating
    setShowGenerateDialog(true);
  };

  const handleGenerateWithOptions = async (options) => {
    try {
      const result = await generateDigestMutation.mutateAsync(options);
      setDigest(result);
    } catch (error) {
      console.error('Failed to generate digest:', error);
    }
  };

  const isLoading = generateDigestMutation.isPending || latestLoading;
  const hasNewEmails = latestReport?.has_new_emails && digest?.id === latestReport?.id;

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BoswellLogo className="h-6 w-6" />
            Your Boswell Brief
          </CardTitle>
          <Button
            size="sm"
            onClick={handleGenerateDigest}
            disabled={isLoading}
            variant={hasNewEmails ? "default" : confirmRefresh ? "destructive" : "outline"}
            className={hasNewEmails ? "animate-pulse" : ""}
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : hasNewEmails ? (
              <>
                <Mail className="h-4 w-4 mr-2" />
                {latestReport.new_emails_count} New Email{latestReport.new_emails_count !== 1 ? 's' : ''}
              </>
            ) : confirmRefresh ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Are you sure? No new emails
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {digest ? 'Refresh' : 'Generate Report'}
              </>
            )}
          </Button>
        </div>
        {digest && (
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date(digest.generated_at).toLocaleString()}
            </p>
            {!hasNewEmails && (
              <Badge variant="secondary" className="text-xs flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Up to date
              </Badge>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-12">
            <LoadingSpinner />
            <p className="text-center text-sm text-muted-foreground mt-4">
              Analyzing your emails and generating insights...
            </p>
          </div>
        ) : digest ? (
          <div className="space-y-4">
            {/* Conversational Summary */}
            <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border">
              <div className="prose dark:prose-invert max-w-none">
                <div className="text-base leading-relaxed">
                  <EmailReferencesText text={digest.summary} />
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* View Emails Button */}
              {digest.emails && digest.emails.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEmailOverlay(true)}
                  className="flex items-center gap-2 justify-center h-auto py-3"
                >
                  <Eye className="h-4 w-4" />
                  <div>
                    <div className="font-bold">{digest.stats?.total || 0}</div>
                    <div className="text-xs text-muted-foreground">View Emails</div>
                  </div>
                </Button>
              )}

              {digest.pending_replies && digest.pending_replies.length > 0 && (
                <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800 text-center">
                  <Mail className="h-5 w-5 text-orange-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {digest.pending_replies.length}
                  </div>
                  <div className="text-xs text-muted-foreground">Need Reply</div>
                </div>
              )}

              {digest.upcoming_events && digest.upcoming_events.length > 0 && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 text-center">
                  <Calendar className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {digest.upcoming_events.length}
                  </div>
                  <div className="text-xs text-muted-foreground">Upcoming Events</div>
                </div>
              )}

              {digest.stats?.spam > 0 && (
                <div className="p-3 bg-white dark:bg-gray-900 rounded-lg border text-center">
                  <div className="text-2xl font-bold text-muted-foreground">
                    {digest.stats.spam}
                  </div>
                  <div className="text-xs text-muted-foreground">Spam Filtered</div>
                </div>
              )}
            </div>

            {/* Pending Actions */}
            {digest.pending_actions && digest.pending_actions.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  Actions Awaiting Approval
                </h4>
                <div className="space-y-2">
                  {digest.pending_actions.map((suggestion) => (
                    <ActionApprovalCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onResolved={(id) => {
                        setDigest(prev => prev ? {
                          ...prev,
                          pending_actions: prev.pending_actions.filter(a => a.id !== id)
                        } : prev);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Plugin Highlights */}
            {digest.plugin_insights?.highlights && digest.plugin_insights.highlights.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <BoswellLogo className="h-4 w-4" />
                  Interesting Items
                </h4>
                <div className="space-y-2">
                  {digest.plugin_insights.highlights.slice(0, 5).map((highlight, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-white dark:bg-gray-900 rounded-lg border flex items-start gap-3"
                    >
                      {highlight.type === 'price' ? (
                        <DollarSign className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Package className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{highlight.subject}</p>
                        <p className="text-xs text-muted-foreground">
                          From: {highlight.from}
                        </p>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {highlight.data}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Insights */}
            {digest.ai_insights && digest.ai_insights.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <BoswellLogo className="h-4 w-4" />
                  AI Insights
                </h4>
                <div className="space-y-2">
                  {digest.ai_insights.map((insight, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800"
                    >
                      <p className="text-sm font-medium mb-1">{insight.subject}</p>
                      <p className="text-xs text-muted-foreground">{insight.insight}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category Breakdown */}
            {digest.stats && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  View category breakdown
                </summary>
                <div className="mt-2 p-3 bg-white dark:bg-gray-900 rounded border space-y-1">
                  {Object.entries(digest.stats)
                    .filter(([key, val]) => key !== 'total' && val > 0)
                    .map(([category, count]) => (
                      <div key={category} className="flex justify-between text-xs">
                        <span className="capitalize">{category}</span>
                        <Badge variant="secondary" className="text-xs">
                          {count}
                        </Badge>
                      </div>
                    ))}
                </div>
              </details>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <BoswellLogo className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Ready to Brief You</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Click the button above to generate your personalized secretary report with AI
              insights, interesting deals, and action items.
            </p>
          </div>
        )}
      </CardContent>

      <GenerateDigestDialog
        isOpen={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        onGenerate={handleGenerateWithOptions}
        sinceReportId={digest?.id}
      />

      {/* Email List Overlay */}
      {showEmailOverlay && digest?.emails && (
        <EmailListOverlay
          emailIds={digest.emails.map(e => e.id)}
          title={`Report Emails (${digest.emails.length})`}
          onClose={() => setShowEmailOverlay(false)}
        />
      )}
    </Card>
  );
}
